import React from 'react';
import { useLocation } from 'react-router-dom';

export const BUILD_CHECK_INTERVAL_MS = 300000;

export function isNewerBuild(loaded: unknown, live: unknown): boolean {
  return (
    typeof loaded === 'string' &&
    typeof live === 'string' &&
    loaded !== '' &&
    live !== '' &&
    loaded !== live
  );
}

const loadedClientJs = (): unknown =>
  (window as unknown as { __ASSET_MANIFEST__?: { client_js?: unknown } })
    .__ASSET_MANIFEST__?.client_js;

export async function fetchLiveClientJs(): Promise<unknown> {
  try {
    const res = await fetch('/build-info', {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      return undefined;
    }
    return ((await res.json()) as { client_js?: unknown })?.client_js;
  } catch {
    return undefined;
  }
}

interface Options {
  reload?: () => void;
  check?: () => Promise<unknown>;
  intervalMs?: number;
}

// After a deploy, the next change of page loads the new build in full, so it never asks for chunks the old build named.
export function useStaleBuildReload({
  reload = () => window.location.reload(),
  check = fetchLiveClientJs,
  intervalMs = BUILD_CHECK_INTERVAL_MS,
}: Options = {}): void {
  const { pathname } = useLocation();
  const stale = React.useRef(false);
  const lastPathname = React.useRef(pathname);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (stale.current) {
        return;
      }
      const live = await check();
      if (!cancelled && isNewerBuild(loadedClientJs(), live)) {
        stale.current = true;
      }
    };
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') {
        run().catch(() => undefined);
      }
    };
    const timer = window.setInterval(() => {
      run().catch(() => undefined);
    }, intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [check, intervalMs]);

  // Only a change of page reloads: a search-param change on the same page may sit beside unsaved input.
  React.useEffect(() => {
    if (pathname === lastPathname.current) {
      return;
    }
    lastPathname.current = pathname;
    if (stale.current) {
      reload();
    }
  }, [pathname, reload]);
}
